import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ArrowDown, Star } from "lucide-react";
import { BUSINESS, IMAGES } from "@/data";

const EASE = [0.16, 1, 0.3, 1];

const Line = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, ease: EASE, delay }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative min-h-screen overflow-hidden border-b border-black pt-24">
      <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-0 px-4 sm:px-8 lg:grid-cols-12">
        {/* Left copy */}
        <div className="lg:col-span-7 flex flex-col justify-center py-12 lg:py-24">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="mb-6 flex flex-wrap items-center gap-4"
          >
            <span className="border border-black px-3 py-1 text-xs font-bold uppercase tracking-[0.2em]">
              {BUSINESS.county}
            </span>
            <span className="flex items-center gap-1 text-xs font-bold uppercase tracking-[0.15em]">
              <Star size={14} className="fill-[#FF4E00] text-[#FF4E00]" /> {BUSINESS.rating} · {BUSINESS.reviewCount} Reviews
            </span>
          </motion.div>

          <h1 className="font-anton uppercase leading-[0.85] tracking-tighter text-[15vw] sm:text-[12vw] lg:text-[7.5vw]">
            <Line delay={0.15}>Dumpster</Line>
            <Line delay={0.28}>Rental &</Line>
            <Line delay={0.41}><span className="text-[#FF4E00]">Junk</span> Removal</Line>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.8, ease: EASE }}
            className="mt-8 max-w-md text-base lg:text-lg text-muted-foreground"
          >
            Same-day roll-off dumpsters and full-service waste pickup across {BUSINESS.county}. {BUSINESS.hours}, seven days a week.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.8, ease: EASE }}
            className="mt-10 flex flex-col gap-4 sm:flex-row"
          >
            <button
              data-testid="hero-book-btn"
              onClick={() => scrollTo("book")}
              className="hard-shadow bg-[#FF4E00] px-8 py-5 font-anton text-xl uppercase tracking-wide text-white transition-transform hover:translate-x-1 hover:translate-y-1 hover:shadow-none"
            >
              Book Now
            </button>
            <a
              data-testid="hero-call-btn"
              href={`tel:${BUSINESS.phoneRaw}`}
              className="flex items-center justify-center gap-2 border-2 border-black px-8 py-5 font-anton text-xl uppercase tracking-wide transition-colors hover:bg-black hover:text-white"
            >
              <Phone size={18} /> {BUSINESS.phone}
            </a>
          </motion.div>
        </div>

        {/* Right image */}
        <div className="relative lg:col-span-5 min-h-[45vh] lg:min-h-full overflow-hidden border-t border-black lg:border-t-0 lg:border-l">
          <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0">
            <img
              src={IMAGES.hero}
              alt="Construction waste and debris ready for haul-off"
              className="h-full w-full object-cover transition-transform duration-700 hover:scale-105"
            />
          </motion.div>
          <div className="absolute bottom-0 left-0 bg-black px-4 py-3 font-bebas text-2xl tracking-wide text-white">
            SAME-DAY SERVICE
          </div>
        </div>
      </div>

      <motion.button
        onClick={() => scrollTo("services")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
        className="absolute bottom-6 left-1/2 hidden -translate-x-1/2 lg:flex flex-col items-center gap-2 text-xs font-bold uppercase tracking-[0.2em]"
      >
        <ArrowDown size={16} className="animate-bounce" /> Scroll
      </motion.button>
    </section>
  );
};
